import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UNSET_FILTER_VALUE,
  UNSET_FILTER_VALUE_LABEL,
} from '@elementary/common-ui/constants';
import { tableAssetsCoverageFilterOptionsQuery } from 'catalog/services/api';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useTableAssets from '@elementary/common-ui/hooks/useTableAssets';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { TableAssetsFiltersQueryResponse } from 'catalog/components/CoverageTable/types';
import styled from 'styled-components';
import { ValueLabelIconOption } from '@elementary/common-ui/types';

type FilterOption = {
  label: string;
  value: string;
  count: number;
};

const colorMap = {
  green: 'green100',
  orange: 'orange2',
  yellow: 'yellow',
  red: 'red2',
};

const Circle = styled.div<{ $color: 'green' | 'orange' | 'yellow' | 'red' }>`
  background: ${({ theme, $color }) => theme.palette.common[colorMap[$color]]};
  width: 12px;
  height: 12px;
  border-radius: 100%;
`;

const percentageAmountFilterOptions: Array<ValueLabelIconOption> = [
  {
    label: '76-100%',
    value: '76-100%',
    icon: <Circle $color="green" />,
  },
  {
    label: '51-75%',
    value: '51-75%',
    icon: <Circle $color="yellow" />,
  },
  {
    label: '26-50%',
    value: '26-50%',
    icon: <Circle $color="orange" />,
  },
  { label: '0-25%', value: '0-25%', icon: <Circle $color="red" /> },
];

const getFilterOptionOverride = (
  items: Array<{ value: string; count: number }> | undefined,
): Array<FilterOption> =>
  items?.map((item) => ({
    label:
      item.value === UNSET_FILTER_VALUE ? UNSET_FILTER_VALUE_LABEL : item.value,
    value: item.value,
    count: item.count,
  })) ?? [];

export const useCoverageFilterOptions = (daysBack: number) => {
  const { data: tableAssetsMap } = useTableAssets();
  const { treeViewBy } = useTreeViewParams();
  const selectedEnvId = useSelectedEnv();
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();

  const getFilterOptionsQueryArgs = useMemo(() => {
    if (selectedTreeFolderNodes?.length) {
      return {
        filters: { ids: selectedTreeFolderNodes?.map((node) => node.id) },
      };
    }

    return {};
  }, [selectedTreeFolderNodes]);

  const { data: filtersData } = useQuery<TableAssetsFiltersQueryResponse>({
    queryKey: [
      'coverage-table-filters',
      selectedEnvId,
      treeViewBy,
      selectedTreeNode?.id,
      daysBack,
    ],
    queryFn: () =>
      tableAssetsCoverageFilterOptionsQuery(
        getFilterOptionsQueryArgs,
        selectedEnvId!,
        daysBack,
      ),
    enabled: !!selectedEnvId && selectedTreeNode?.resourceType === 'folder',
  });

  const getDimensionFilterOptions = useCallback(
    (dimension: keyof TableAssetsFiltersQueryResponse['dimensionTestNames']) =>
      getFilterOptionOverride(filtersData?.dimensionTestNames[dimension]),
    [filtersData],
  );

  const lineageFilterOptions = useMemo(
    () =>
      Array.from(tableAssetsMap.values()).map((asset) => ({
        label: asset.name || asset.id,
        value: asset.id,
      })),
    [tableAssetsMap],
  );

  const filterOptions = useMemo(
    () => ({
      names: getFilterOptionOverride(filtersData?.names),
      dbNames: getFilterOptionOverride(filtersData?.dbNames),
      schemaNames: getFilterOptionOverride(filtersData?.schemaNames),
      percentageAmount: percentageAmountFilterOptions,
      freshness: getDimensionFilterOptions('freshness'),
      completeness: getDimensionFilterOptions('completeness'),
      uniqueness: getDimensionFilterOptions('uniqueness'),
      validity: getDimensionFilterOptions('validity'),
      accuracy: getDimensionFilterOptions('accuracy'),
      consistency: getDimensionFilterOptions('consistency'),
      other: getDimensionFilterOptions('other'),
      tags: getFilterOptionOverride(filtersData?.tags),
      owners: getFilterOptionOverride(filtersData?.owners),
      testNames: getFilterOptionOverride(filtersData?.testNames),
      assetsLineage: lineageFilterOptions,
      criticality: getFilterOptionOverride(filtersData?.criticality),
    }),
    [filtersData, getDimensionFilterOptions, lineageFilterOptions],
  );

  return {
    filterOptions,
    filtersData,
  };
};
